import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import type { DashboardRecent, DashboardSummary } from '../services/types';
import { formatDate, formatMoney } from '../utils/format';

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

export function DashboardPage() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [recent, setRecent] = useState<DashboardRecent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recentError, setRecentError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [sRes, rRes] = await Promise.all([
        api.getDashboardSummary(),
        api.getDashboardRecent(8),
      ]);
      if (sRes.data) setSummary(sRes.data);
      else setError(sRes.error?.message ?? 'Ошибка загрузки сводки');
      if (rRes.data) setRecent(rRes.data);
      else setRecentError(rRes.error?.message ?? 'Не удалось загрузить последние события');
    })();
  }, []);

  if (error && !summary) {
    return <p style={{ color: 'var(--danger)' }}>{error}</p>;
  }

  if (!summary) {
    return <p style={{ color: 'var(--text-muted)' }}>Загрузка dashboard…</p>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
      </div>

      <div className="grid-stats" style={{ marginBottom: '1.5rem' }}>
        <StatCard label="Пользователей" value={summary.total_users} />
        <StatCard label="Активных подписок" value={summary.active_subscriptions} />
        <StatCard label="Истёкших подписок" value={summary.expired_subscriptions} />
        <StatCard label="Без подписки" value={summary.no_subscription} />
        <StatCard label="VPN ключей" value={summary.active_vpn_keys} />
        <StatCard label="Серверов (enabled)" value={summary.enabled_servers} />
        <StatCard label="Premium / Default" value={`${summary.premium_users} / ${summary.default_users}`} />
        <StatCard label="Доход сегодня" value={formatMoney(summary.revenue.today)} />
        <StatCard label="Доход за месяц" value={formatMoney(summary.revenue.month)} />
      </div>

      <div className="dashboard-split">
        <div className="card">
          <h3 style={{ marginTop: 0 }}>По tier</h3>
          {summary.tier_distribution.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>Нет данных</p>
          ) : (
            summary.tier_distribution.map((t) => (
              <div key={t.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span>{t.label}</span>
                <strong>{t.count}</strong>
              </div>
            ))
          )}
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>По регионам (VPN)</h3>
          {summary.region_distribution.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>Нет данных</p>
          ) : (
            summary.region_distribution.map((r) => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span>{r.label.toUpperCase()}</span>
                <strong>{r.count}</strong>
              </div>
            ))
          )}
        </div>
      </div>

      {recentError && !recent && (
        <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>{recentError}</p>
      )}

      {recent && (
        <div className="dashboard-recent">
          <RecentBlock title="Последние платежи" seeAllTo="/payments">
            {recent.payments.map((p) => (
              <div key={p.id} style={{ marginBottom: 8, fontSize: '0.85rem' }}>
                <Link to={`/payments/${encodeURIComponent(p.id)}`}>{p.id.slice(0, 20)}…</Link>
                {' · '}
                {formatMoney(p.amount)} · {p.status} · {formatDate(p.created_at)}
                {' · '}
                <Link to={`/users/${p.user_id}`}>user {p.user_id}</Link>
              </div>
            ))}
          </RecentBlock>
          <RecentBlock title="Последние VPN ключи" seeAllTo="/vpn">
            {recent.vpn_keys.map((k) => (
              <div key={`${k.protocol ?? 'wg'}-${k.id}`} style={{ marginBottom: 8, fontSize: '0.85rem' }}>
                <span style={{ opacity: 0.75 }}>{(k.protocol ?? 'wg').toUpperCase()}</span>
                {' · '}
                {k.protocol === 'ovpn' ? (
                  <Link to={`/users/${k.user_id}`}>
                    {k.server_country.toUpperCase()} #{k.client_id}
                  </Link>
                ) : (
                  <Link to={`/vpn/${k.id}`}>
                    {k.server_country.toUpperCase()} #{k.client_id}
                  </Link>
                )}
                {' · '}
                <Link to={`/users/${k.user_id}`}>user {k.user_id}</Link>
                {' · '}
                {formatDate(k.created_at)}
              </div>
            ))}
          </RecentBlock>
          <RecentBlock title="Последние промокоды" seeAllTo="/promos">
            {recent.promos.map((p) => (
              <div key={`${p.code}-${p.user_id}`} style={{ marginBottom: 8, fontSize: '0.85rem' }}>
                <strong>{p.code}</strong> ·{' '}
                <Link to={`/users/${p.user_id}`}>user {p.user_id}</Link> · {formatDate(p.used_at)}
              </div>
            ))}
          </RecentBlock>
          <RecentBlock title="Последние инвайты" seeAllTo="/users">
            {recent.invites.map((i) => (
              <div
                key={`${i.code}-${i.owner_id}-${i.used_by ?? 'open'}-${i.used_at ?? ''}`}
                style={{ marginBottom: 8, fontSize: '0.85rem' }}
              >
                <strong>{i.code}</strong>
                {' · '}+{i.bonus_days} дн. ({i.subscription_tier})
                {' · '}
                owner <Link to={`/users/${i.owner_id}`}>{i.owner_id}</Link>
                {i.used_by != null && (
                  <>
                    {' · '}
                    used by <Link to={`/users/${i.used_by}`}>{i.used_by}</Link>
                  </>
                )}
                {' · '}
                {i.status}
                {i.used_at ? ` · ${formatDate(i.used_at)}` : ''}
              </div>
            ))}
          </RecentBlock>
        </div>
      )}
    </div>
  );
}

function RecentBlock({
  title,
  seeAllTo,
  children,
}: {
  title: string;
  seeAllTo?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <h3 style={{ marginTop: 0, marginBottom: 0, fontSize: '1rem' }}>{title}</h3>
        {seeAllTo ? (
          <Link to={seeAllTo} style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
            Все →
          </Link>
        ) : null}
      </div>
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}
